import { motion } from "framer-motion";
import { Sparkles, TrendingUp, RefreshCw, Lightbulb } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { useBusinessInsights, useRefreshInsights } from "./api";

// ============================================================================
// REPORT-05 — AI-suggested insights. "Reports we don't even know we need."
// Pegasus computes notable signals from the mirror, then Gemini writes a short
// briefing over those exact numbers. Every card shows its raw metric so the
// owner can trust it — the AI narrates, it doesn't invent.
// ============================================================================

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

export function InsightsPanel() {
  const { data, isLoading, isError } = useBusinessInsightsSafe();
  // Wave-1: Refresh = force a regenerate server-side (the page itself loads the
  // persisted briefing instantly; this is the only path that recomputes).
  const refresh = useRefreshInsights();

  return (
    <div>
      {isError ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-600">Couldn't generate insights right now. Try again in a moment.</p></Card>
      ) : isLoading ? (
        <Card className="p-10 text-center">
          <Sparkles className="mx-auto h-8 w-8 animate-pulse text-brand-400" />
          <p className="mt-3 text-sm text-slate-500">Scanning the book and writing your briefing…</p>
        </Card>
      ) : (
        <>
          {/* AI briefing */}
          {data?.narrative && (
            <Card className="mb-4 p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Lightbulb className="h-4 w-4" /></span>
                <div>
                  <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    The briefing
                    <InfoTip className="normal-case" label="How the briefing is made">The briefing looks through your data for things worth noticing, such as revenue swings, the R-454B transition, members owed a visit and your busiest zones. Every number is computed from your data; AI only writes the words around it.</InfoTip>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-800">{data.narrative}</p>
                </div>
              </div>
            </Card>
          )}

          {/* Insight cards */}
          <div className="grid gap-3 sm:grid-cols-2">
            {(data?.facts ?? []).map((f, i) => (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: EASE_OUT_EXPO, delay: Math.min(i * 0.05, 0.3) }}
              >
                <Card className="h-full p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-slate-900">{f.title}</h3>
                    <TrendingUp className="h-4 w-4 flex-shrink-0 text-brand-400" />
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{f.detail}</p>
                  <div className="mt-2.5 inline-flex items-center rounded-md bg-slate-50 px-2 py-0.5 text-xs font-medium tabular-nums text-slate-500">{f.metric}</div>
                </Card>
              </motion.div>
            ))}
          </div>

          {(data?.facts ?? []).length === 0 && (
            <Card className="p-10 text-center"><p className="text-sm text-slate-500">No standout signals right now — the book looks steady.</p></Card>
          )}

          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-slate-400">
              {data?.generated_at ? `Generated ${new Date(data.generated_at).toLocaleString()}` : ""}
            </span>
            <button type="button" onClick={() => refresh.mutate()} disabled={refresh.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-brand-300 hover:text-brand-700 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${refresh.isPending ? "animate-spin" : ""}`} /> {refresh.isPending ? "Regenerating…" : "Refresh"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Small wrapper to keep the hook name tidy + expose isFetching for the refresh spinner.
function useBusinessInsightsSafe() {
  const q = useBusinessInsights();
  return { data: q.data, isLoading: q.isLoading, isError: q.isError, isFetching: q.isFetching, refetch: q.refetch };
}
